import { makeAutoObservable } from 'mobx';
import { ExportStep, exportFlows } from '../config';
import { KeyofNamespace } from '../../../translations/keyofNs';
import {
  ExportRequest,
  CreateEmployeeExportRequest,
  CreatePayrollExportRequest,
  CreateReceiptExportRequest,
  CreateVoucherExportRequest,
  EmployeeQueryRequest,
  MonthFieldRequest,
  BenefitFilterRequest,
  CreatePayrollExportFormatEnum,
  ReceiptExportFormatEnum,
  ExportTypeEnum
} from '@pxtra-client-lib-legacy/gen';

export type ExportFormatEnum = CreatePayrollExportFormatEnum | ReceiptExportFormatEnum;
export type ExportFlowType = keyof typeof exportFlows;
export type SupportedExportTypes = Exclude<ExportTypeEnum, 'bav_contracts' | 'jobbike_contracts'>;

type ExportData = {
  employee_data: Partial<CreateEmployeeExportRequest>;
  payroll_data: Partial<CreatePayrollExportRequest>;
  receipts: Partial<CreateReceiptExportRequest>;
  voucher_orders: Partial<CreateVoucherExportRequest>;
};

export class ExportFlowStore {
  private _currentType: ExportFlowType | null = null;
  private _currentStep = 0;
  private _data: Partial<ExportRequest> = {};
  private _isLoading = false;

  constructor() {
    makeAutoObservable(this);
  }

  public get currentType() {
    return this._currentType;
  }

  public get currentStep() {
    return this._currentStep;
  }

  public get data() {
    return this._data;
  }

  private getTypedData<T extends keyof ExportData>(): ExportData[T] {
    return this._data as ExportData[T];
  }

  public initializeFlow(type: ExportFlowType) {
    this._currentType = type;
    this._currentStep = 0;
    this._data = {};
  }

  public getIsInitialized(type: ExportFlowType) {
    return this._currentType === type;
  }

  public resetFlow() {
    this._currentType = null;
    this._currentStep = 0;
    this._data = {};
  }

  public updateData(data: Partial<Record<string, unknown>>) {
    this._data = { ...this._data, ...data };
  }

  public hasSelectedEmployees() {
    return (
      ((this._data as { employees_query?: { ids?: string[] } }).employees_query?.ids?.length ?? 0) >
      0
    );
  }

  public hasSelectedFormat() {
    return (this._data as { format?: string }).format;
  }

  public getAllowedFormats<T extends ExportFlowType>() {
    if (!this._currentType) {
      throw new Error('Export type not initialized');
    }

    switch (this._currentType) {
      case 'receipts':
      case 'voucher_orders':
      case 'employee_data': {
        const formats: Array<ReceiptExportFormatEnum> = ['xlsx', 'csv'];
        return formats as T extends 'payroll_data'
          ? Array<CreatePayrollExportFormatEnum>
          : Array<ReceiptExportFormatEnum>;
      }
      case 'payroll_data': {
        const formats: Array<CreatePayrollExportFormatEnum> = [
          'xlsx',
          'csv',
          'datev',
          'agenda',
          'agenda_V26_0_R1',
          'sage',
          'personio',
          'addison',
          'loga3',
          'sap'
        ];
        return formats as T extends 'payroll_data'
          ? Array<CreatePayrollExportFormatEnum>
          : Array<ReceiptExportFormatEnum>;
      }
      default:
        throw new Error(`Unsupported export type: ${this._currentType}`);
    }
  }

  public buildExportRequest(companyId: string): ExportRequest {
    if (!this._currentType) {
      throw new Error('Export type not initialized');
    }

    switch (this._currentType) {
      case 'employee_data': {
        const data = this.getTypedData<'employee_data'>();
        const request: CreateEmployeeExportRequest = {
          export_type: 'employee_data',
          company: companyId,
          format: data.format as ReceiptExportFormatEnum,
          employees_query: data.employees_query as EmployeeQueryRequest
        };
        return request;
      }
      case 'payroll_data': {
        const data = this.getTypedData<'payroll_data'>();
        const request: CreatePayrollExportRequest = {
          export_type: 'payroll_data',
          company: companyId,
          format: data.format as CreatePayrollExportFormatEnum,
          employees_query: data.employees_query as EmployeeQueryRequest,
          accounting_period_from: data.accounting_period_from as MonthFieldRequest,
          accounting_period_to: data.accounting_period_to as MonthFieldRequest,
          filter: data.filter as BenefitFilterRequest | null
        };
        return request;
      }
      case 'receipts': {
        const data = this.getTypedData<'receipts'>();
        const request: CreateReceiptExportRequest = {
          export_type: 'receipts',
          company: companyId,
          format: data.format as ReceiptExportFormatEnum,
          employees_query: data.employees_query as EmployeeQueryRequest,
          accounting_period_from: data.accounting_period_from as MonthFieldRequest,
          accounting_period_to: data.accounting_period_to as MonthFieldRequest,
          filter: data.filter as BenefitFilterRequest | null
        };
        return request;
      }
      case 'voucher_orders': {
        const data = this.getTypedData<'voucher_orders'>();
        const request: CreateVoucherExportRequest = {
          export_type: 'voucher_orders',
          company: companyId,
          format: data.format as ReceiptExportFormatEnum,
          accounting_period_from: data.accounting_period_from as MonthFieldRequest
        };
        return request;
      }
      default:
        throw new Error(`Unsupported export type: ${this._currentType}`);
    }
  }

  public getCurrentStep(index?: number): ExportStep | null {
    if (!this._currentType) return null;
    this._currentStep = index ?? this._currentStep;
    return exportFlows[this._currentType].steps[this._currentStep];
  }

  public async validateCurrentStep(): Promise<true | KeyofNamespace<'export'>> {
    if (!this._currentType) return true;

    const currentStep = exportFlows[this._currentType].steps[this._currentStep];
    if (!currentStep.validation) return true;

    try {
      this._isLoading = true;
      return await currentStep.validation(this._data);
    } finally {
      this._isLoading = false;
    }
  }
}
